// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.

import React from "react";
import "./App.css";
import * as microsoftTeams from "@microsoft/teams-js";
import { BrowserRouter as Router, Route } from "react-router-dom";
import Privacy from "./Privacy";
import TermsOfUse from "./TermsOfUse";
import Tab from "./Home/Home";
import ConsentPopup from "./ConsentPopup";
import ClosePopup from "./ClosePopup";
import TabConfig from "./TabConfig";
import Aux from '../hoc/Auxiliary/Auxiliary'
import Loader from "./UI/Loader/Loader";
import { searchListByName, verifyInGroup } from '../graph-services/GraphServices'

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
export const EventContext = React.createContext();
class App extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            context: {},
            ssoToken: "",
            listId: "",
            site: {
                id: 'apricocom.sharepoint.com,2c360fec-d9df-4288-a2ff-3b0276db5f38,c7fc0b5a-d0a6-487b-a465-52283d18aff3',
                name: 'QwickCV',
                webUrl: 'https://apricocom.sharepoint.com/sites/QwickCV'
            },
            consentRequired: false,
            consentProvided: false,
            graphAccessToken: "",
            sessionExpired: false,
            error: false,
            loading: true
        };
        microsoftTeams.initialize();

        //Bind any functions that need to be passed as callbacks or used to React components
        this.ssoLoginSuccess = this.ssoLoginSuccess.bind(this);
        this.ssoLoginFailure = this.ssoLoginFailure.bind(this);
        this.consentSuccess = this.consentSuccess.bind(this);
        this.consentFailure = this.consentFailure.bind(this);
        this.unhandledFetchError = this.unhandledFetchError.bind(this);
        //  this.callGraphFromClient = this.callGraphFromClient.bind(this);
        this.showConsentDialog = this.showConsentDialog.bind(this);
    }

    // Initialize the Microsoft Teams SDK
    // microsoftTeams.initialize();

    //React lifecycle method that gets called once a component has finished mounting
    //Learn more: https://reactjs.org/docs/react-component.html#componentdidmount
    async componentDidMount() {
        this.resetSession();
    }

    // A session reset will be initiated, when the session expiration value changes to true.
    // Learn more: https://reactjs.org/docs/react-component.html#componentdidupdate
    async componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.state.sessionExpired && this.state.sessionExpired !== prevState.sessionExpired) {
            this.resetSession();
        }
    }

    resetSession = () => {
        // Initialize the Microsoft Teams SDK
        microsoftTeams.initialize();
        // Get the user context from Teams and set it in the state
        microsoftTeams.getContext((context, error) => {
            this.setState({ context: context });
        });

        //Perform Azure AD single sign-on authentication
        let authTokenRequestOptions = {
            successCallback: (result) => {
                this.ssoLoginSuccess(result);
            }, //The result variable is the SSO token.
            failureCallback: (error) => {
                this.ssoLoginFailure(error);
            },
        };

        microsoftTeams.authentication.getAuthToken(authTokenRequestOptions);
    }

    ssoLoginSuccess = async (result) => {
        this.setState({ ssoToken: result });
        this.exchangeClientTokenForServerToken(result);
    };

    ssoLoginFailure(error) {
        this.setState({ error: true });
    }

    //Exchange the SSO access token for a Graph access token
    //Learn more: https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-on-behalf-of-flow
    exchangeClientTokenForServerToken = async (token) => {
        let serverURL = `${process.env.REACT_APP_BASE_URL}/getGraphAccessToken?ssoToken=${token}`;
        let response = await fetch(serverURL).catch(this.unhandledFetchError); //This calls getGraphAccessToken route in /api-server/app.js
        let data;
        if (response) {
            data = await response.json().catch(this.unhandledFetchError);
        } else {
            this.handleLoading(false)
            this.setState({ error: true })
        }

        if (!(response && response.ok)) {
            this.handleLoading(false)
            if (data && data.error === "consent_required") {
                this.setState({ consentRequired: true }); //This displays the consent required message.
                this.showConsentDialog();
            } else {
                this.setState({ error: true })
            }
            //A consent_required error means it's the first time a user is logging into to the app, so they must consent to sharing their Graph data with the app.
            //They may also see this error if MFA is required.
            //Proceed to show the consent dialogue.
        } else {
            //Server side token exchange worked. Save the access_token to state, so that it can be picked up and used by the componentDidMount lifecycle method.
            this.setState({ graphAccessToken: data["access_token"], sessionExpired: false });
            await searchListByName(data["access_token"], this.state.site.id)
                .then(res => {
                    this.setState({
                        listId: res.id
                    })
                })
            if (data["access_token"] !== "") {
                let isConsultant = true

                verifyInGroup(data["access_token"], process.env.REACT_APP_SALES_GROUP_ID, this.state.context.userObjectId).then(r => {
                    if (r.length > 0) {
                        isConsultant = false;
                        this.setState({
                            isSalesMember: true
                        })
                    } else {
                        this.setState({
                            isSalesMember: false
                        })
                    }

                })
                verifyInGroup(data["access_token"], process.env.REACT_APP_HR_GROUP_ID, this.state.context.userObjectId).then(r => {
                    if (r.length > 0) {
                        isConsultant = false;
                        this.setState({
                            isHrMember: true
                        })
                    } else {
                        this.setState({
                            isHrMember: false
                        })
                    }
                })
                verifyInGroup(data["access_token"], process.env.REACT_APP_ADMIN_GROUP_ID, this.state.context.userObjectId).then(r => {
                    if (r.length > 0) {
                        isConsultant = false;
                        this.setState({
                            isAdminMember: true
                        })
                    } else {
                        this.setState({
                            isAdminMember: false
                        })
                    }
                })
            }
        }
    };

    //Show a popup dialogue prompting the user to consent to the required API permissions. This opens ConsentPopup.js.
    //Learn more: https://docs.microsoft.com/en-us/microsoftteams/platform/tabs/how-to/authentication/auth-tab-aad#initiate-authentication-flow
    showConsentDialog() {
        microsoftTeams.authentication.authenticate({
            url: window.location.origin + "/auth-start",
            width: 600,
            height: 535,
            successCallback: (result) => {
                this.consentSuccess(result);
            },
            failureCallback: (reason) => {
                this.consentFailure(reason);
            },
        });
    }


    //Callback function for a successful authorization
    async consentSuccess(result) {
        await searchListByName(result, this.state.site.id)
            .then(res => {
                this.setState({
                    listId: res.id
                })
            })
        if (result !== "") {
            let isConsultant = true

            verifyInGroup(result, process.env.REACT_APP_SALES_GROUP_ID, this.state.context.userObjectId).then(r => {
                if (r.length > 0) {
                    isConsultant = false;
                    this.setState({
                        isSalesMember: true
                    })
                } else {
                    this.setState({
                        isSalesMember: false
                    })
                }

            })
            verifyInGroup(result, process.env.REACT_APP_HR_GROUP_ID, this.state.context.userObjectId).then(r => {
                if (r.length > 0) {
                    isConsultant = false;
                    this.setState({
                        isHrMember: true
                    })
                } else {
                    this.setState({
                        isHrMember: false
                    })
                }
            })
            verifyInGroup(result, process.env.REACT_APP_ADMIN_GROUP_ID, this.state.context.userObjectId).then(r => {
                if (r.length > 0) {
                    isConsultant = false;
                    this.setState({
                        isAdminMember: true
                    })
                } else {
                    this.setState({
                        isAdminMember: false
                    })
                }
            })
        }
        //Save the Graph access token in state
        this.setState({
            graphAccessToken: result,
            consentProvided: true,
            sessionExpired: false,
        });
    }

    consentFailure(reason) {
        this.setState({ error: true });
    }

    //Generic error handler ( avoids having to do async fetch in try/catch block )
    unhandledFetchError(err) {
        this.setState({ error: true });
    }

    handleLoading(value) {
        this.setState({ loading: value });
    }

    checkSession(value) {
        this.setState({ sessionExpired: value })
    }

    showError() {
        if (this.state.error) {
            return <h1>ERROR: A Problem was occured</h1>
        }
        return null
    }

    // Display the app home page hosted in Teams
    render() {
        return (
            <Aux>
                <Router>
                    <Route exact path="/privacy" component={Privacy} />
                    <Route exact path="/termsofuse" component={TermsOfUse} />
                    <Route exact path="/tab" render={() => this.state.site.id !== '' && this.state.listId !== ''
                        && this.state.isHrMember !== undefined && this.state.isAdminMember !== undefined && this.state.isSalesMember !== undefined
                        && <Tab userEmail={this.state.context.upn}
                                userID={this.state.context.userObjectId}
                                access_token={this.state.graphAccessToken}
                                checkSession={(v) => this.checkSession(v)}
                                sessionExpired={this.state.sessionExpired}
                                site={this.state.site}
                                listId={this.state.listId}
                                isAdminMember={this.state.isAdminMember}
                                isHrMember={this.state.isHrMember}
                                isSalesMember={this.state.isSalesMember}
                                salesGroupId={process.env.REACT_APP_SALES_GROUP_ID}
                                adminGroupId={process.env.REACT_APP_ADMIN_GROUP_ID}
                                hrGroupId={process.env.REACT_APP_HR_GROUP_ID}
                                membersGroupId={process.env.REACT_APP_MEMBERS_GROUP_ID}
                                onLoading={(v) => this.handleLoading(v)} />} />
                    <Route exact path="/config" component={TabConfig} />
                    <Route exact path="/auth-start" component={ConsentPopup} />
                    <Route exact path="/auth-end" component={ClosePopup} />
                </Router>
                {this.showError()}
                <Loader open={this.state.loading} onClose={() => this.handleLoading(false)}/>
            </Aux>

        );
    }
}

export default App;
